import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { leadListColumns, lockedLeadColumns } from './leadListColumns'
import { arrangeColumns } from '@user/components/xGrid'

import { useGetDivisionsOnMount } from '@user/store/divisions'
import { useGetLocationTypesOnMount } from '@user/store/locationTypes'
import {
  useGetAllFunnelStagesOnMount,
  useGetSalesFunnelListOnMount,
} from '@user/store/salesFunnel'
import { useGetSourcesOnMount } from '@user/store/sources'
import { useGetCampaignsOnMount } from '@user/store/campaigns'
import { useGetSalesTeamListOnMount } from '@user/store/salesTeam'
import {
  selectLeadColumnOrder,
  useLoadListViewPreferencesOnce,
} from '@user/store/userPreferences'
import {
  useGetCustomFieldsOnMount,
  useSelectCustomFieldData,
} from '@user/store/customFields'
import {
  selectAllTargetPests,
  useGetTargetPestsOnMount,
} from '@user/store/targetPests'
import { useGetUserListOnMount } from '@user/store/users'
import { useAddMissingColumns } from '@user/components/xGrid/valueFormatters/syncNewColumns'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'

export const useGetColumnData = () => {
  const divStatus = useGetDivisionsOnMount()
  const locTypeStatus = useGetLocationTypesOnMount()
  const funnelStatus = useGetSalesFunnelListOnMount()
  const stageStatus = useGetAllFunnelStagesOnMount()
  const teamStatus = useGetSalesTeamListOnMount()
  const sourceStatus = useGetSourcesOnMount()
  const campStatus = useGetCampaignsOnMount()
  const userPrefsLoading = useLoadListViewPreferencesOnce()
  const allUsersStatus = useGetUserListOnMount()
  const customFieldsLoading = useGetCustomFieldsOnMount()

  const { customFields } = useSelectCustomFieldData()
  const targetPests = useGetTargetPestsOnMount()
  const allTargetPests = useSelector(selectAllTargetPests)

  const mapViewEnabled = useMapViewFeatureEnabled()
  const d2DKnockEnabled = useD2DKnockEnabled()
  const knockStatusEnabled = mapViewEnabled && d2DKnockEnabled

  const columnOrder = useSelector(selectLeadColumnOrder)
  const [columns, columnVisibility] = useMemo(
    () =>
      arrangeColumns(
        columnOrder,
        leadListColumns(allTargetPests, true, knockStatusEnabled),
        lockedLeadColumns,
        customFields,
      ),
    [columnOrder, customFields, allTargetPests, knockStatusEnabled],
  )

  const { syncNewColumnsWithPreferences } = useAddMissingColumns({
    columnOrder,
    columns,
    newlyAddedColumns: [
      'customerReferralLocationCode',
      'employeeReferral',
      'otherReferral',
      ...(knockStatusEnabled ? ['knockStatus', 'knockStatusReason'] : []),
    ],
    preference: 'lead',
  })
  const loading =
    divStatus === 'pending' ||
    locTypeStatus === 'pending' ||
    funnelStatus === 'pending' ||
    stageStatus === 'pending' ||
    teamStatus === 'pending' ||
    sourceStatus === 'pending' ||
    campStatus === 'pending' ||
    targetPests === 'pending' ||
    allUsersStatus === 'pending' ||
    userPrefsLoading ||
    customFieldsLoading

  useEffect(() => {
    syncNewColumnsWithPreferences()
  }, [columnOrder, syncNewColumnsWithPreferences])

  return { loading, columns, columnVisibility }
}
